import React from "react";
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  SimpleGrid,
  Icon,
  useColorModeValue,
  Badge,
  Divider,
  Button,
  Link,
  HStack,
  Image,
} from "@chakra-ui/react";
import {
  FiDroplet,
  FiGift,
  FiStar,
  FiAward,
  FiCheckCircle,
  FiPackage,
  FiClock,
  FiHeart,
  FiShield,
  FiTruck,
  FiHeadphones,
  FiSettings,
} from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";

const About: React.FC = () => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Box>
      {/* Hero Section */}
      <Box
        bg={useColorModeValue("purple.50", "gray.900")}
        py={20}
        position="relative"
        overflow="hidden"
      >
        <Container maxW="1400px">
          <VStack spacing={8} align="center" textAlign="center">
            <Badge colorScheme="purple" fontSize="md" px={3} py={1}>
              Luxury Fragrances
            </Badge>
            <Heading
              size="2xl"
              bgGradient="linear(to-r, purple.500, purple.700)"
              bgClip="text"
              fontWeight="bold"
            >
              About Luxe Perfumes
            </Heading>
            <Text fontSize="xl" color="gray.600" maxW="800px">
              Your destination for exquisite fragrances and luxury perfumes. We're committed to bringing you the finest scents from around the world, crafted with passion and precision.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="1400px" py={16}>
        <VStack spacing={16} align="stretch">
          {/* Our Story Section */}
          <Box>
            <VStack spacing={8} align="stretch">
              <Heading size="xl">Our Story</Heading>
              <Text fontSize="lg" color="gray.600">
                Founded in 2020, Luxe Perfumes emerged from a passion for creating exceptional fragrances and a vision to provide luxury perfumes to discerning customers. What started as a boutique in New York has grown into a global brand dedicated to bringing the finest scents to fragrance enthusiasts worldwide.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                <Box
                  bg={bgColor}
                  p={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <VStack spacing={4} align="start">
                    <Icon as={FiAward} w={8} h={8} color="purple.500" />
                    <Heading size="md">Premium Quality</Heading>
                    <Text color="gray.600">
                      We source the finest ingredients from around the world, ensuring every fragrance meets our exacting standards of quality and luxury.
                    </Text>
                  </VStack>
                </Box>
                <Box
                  bg={bgColor}
                  p={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <VStack spacing={4} align="start">
                    <Icon as={FiStar} w={8} h={8} color="purple.500" />
                    <Heading size="md">Expert Craftsmanship</Heading>
                    <Text color="gray.600">
                      Our master perfumers create unique scent combinations that reflect our dedication to excellence and innovation.
                    </Text>
                  </VStack>
                </Box>
                <Box
                  bg={bgColor}
                  p={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <VStack spacing={4} align="start">
                    <Icon as={FiClock} w={8} h={8} color="purple.500" />
                    <Heading size="md">Timeless Appeal</Heading>
                    <Text color="gray.600">
                      Each fragrance is crafted to create lasting impressions and become a cherished part of your personal story.
                    </Text>
                  </VStack>
                </Box>
              </SimpleGrid>
            </VStack>
          </Box>

          <Divider />

          {/* Why Choose Us Section */}
          <Box>
            <VStack spacing={8} align="stretch">
              <Heading size="xl">Why Choose Us</Heading>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                <Box
                  bg={bgColor}
                  p={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <VStack spacing={4} align="center" textAlign="center">
                    <Icon as={FiShield} w={8} h={8} color="purple.500" />
                    <Heading size="md">Authentic Scents</Heading>
                    <Text color="gray.600">
                      100% genuine fragrances with premium ingredients
                    </Text>
                  </VStack>
                </Box>
                <Box
                  bg={bgColor}
                  p={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <VStack spacing={4} align="center" textAlign="center">
                    <Icon as={FiTruck} w={8} h={8} color="purple.500" />
                    <Heading size="md">Luxury Delivery</Heading>
                    <Text color="gray.600">
                      Elegant packaging with free shipping on orders above $200
                    </Text>
                  </VStack>
                </Box>
                <Box
                  bg={bgColor}
                  p={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <VStack spacing={4} align="center" textAlign="center">
                    <Icon as={FiHeadphones} w={8} h={8} color="purple.500" />
                    <Heading size="md">Expert Guidance</Heading>
                    <Text color="gray.600">
                      Personalized fragrance recommendations
                    </Text>
                  </VStack>
                </Box>
                <Box
                  bg={bgColor}
                  p={6}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <VStack spacing={4} align="center" textAlign="center">
                    <Icon as={FiPackage} w={8} h={8} color="purple.500" />
                    <Heading size="md">Easy Returns</Heading>
                    <Text color="gray.600">
                      30-day satisfaction guarantee
                    </Text>
                  </VStack>
                </Box>
              </SimpleGrid>
            </VStack>
          </Box>

          <Divider />

          {/* Our Commitment Section */}
          <Box>
            <VStack spacing={8} align="stretch">
              <Heading size="xl">Our Commitment</Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                <Box>
                  <VStack spacing={4} align="start">
                    <Heading size="md">Customer Experience</Heading>
                    <Text color="gray.600">
                      We prioritize creating an exceptional shopping experience for our customers. Our dedicated team ensures you find the perfect fragrance that matches your style and preferences.
                    </Text>
                    <Button
                      as={RouterLink}
                      to="/contact"
                      colorScheme="purple"
                      leftIcon={<FiCheckCircle />}
                    >
                      Contact Us
                    </Button>
                  </VStack>
                </Box>
                <Box>
                  <VStack spacing={4} align="start">
                    <Heading size="md">Quality Assurance</Heading>
                    <Text color="gray.600">
                      Every fragrance in our collection undergoes rigorous testing to ensure the highest quality. We partner with renowned perfumers and use only the finest ingredients.
                    </Text>
                    <Button
                      as={RouterLink}
                      to="/products"
                      colorScheme="purple"
                      leftIcon={<FiPackage />}
                    >
                      Explore Collection
                    </Button>
                  </VStack>
                </Box>
              </SimpleGrid>
            </VStack>
          </Box>

          {/* CTA Section */}
          <Box
            bg={useColorModeValue("purple.50", "gray.900")}
            p={8}
            borderRadius="lg"
            textAlign="center"
          >
            <VStack spacing={4}>
              <Heading size="xl">Discover Your Signature Scent</Heading>
              <Text fontSize="lg" color="gray.600" maxW="600px">
                Browse our collection of luxury fragrances and find the perfect scent that tells your story.
              </Text>
              <Button
                as={RouterLink}
                to="/products"
                colorScheme="purple"
                size="lg"
                leftIcon={<FiPackage />}
              >
                Explore Collection
              </Button>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default About;
